import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mvpApi } from '../services/api';
import { getRefFromUrl, getPathWithRef, getStoredPartnerToken } from '../utils/attribution';
import NotFound404 from '../pages/NotFound404';

const VALID_REF_KEY = 'mvp_valid_ref';
const SSO_ERROR_VIEW_KEY = 'mvp_sso_error_view';
const LOCALES = new Set(['es', 'en', 'fr', 'ar', 'zh']);

function stripLocalePrefix(path) {
  const parts = String(path || '').split('/').filter(Boolean);
  if (parts.length > 0 && LOCALES.has(parts[0])) {
    return `/${parts.slice(1).join('/') || ''}`.replace(/\/+$/, '') || '/';
  }
  return String(path || '').replace(/\/+$/, '') || '/';
}

/**
 * Allow access only with valid ?ref= partner code (validated via API).
 * Exceptions: /login, /auth/callback (OAuth return), authenticated /register, /es/regularizacion/success, /intake, /organizer, SSO signup steps, /advisor (and legacy /admin redirect targets).
 */
export default function RefGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const path = location.pathname;
    const basePath = stripLocalePrefix(path);
    const isSalesLandingPath = path === '/' || path === '/es/regularizacion';
    const token = localStorage.getItem('authToken');
    const searchParams = new URLSearchParams(location.search || '');

    // Sales landing path with no ref in URL: restore ref from storage so referral persists across reloads.
    if (isSalesLandingPath && !searchParams.get('ref')?.trim() && getStoredPartnerToken()) {
      navigate(getPathWithRef(path), { replace: true });
      return;
    }

    // Dev-only bypass for local UI work without a backend.
    if (process.env.NODE_ENV !== 'production' && searchParams.get('bypass') === '1') {
      setAllowed(true);
      setChecking(false);
      return;
    }

    if (basePath === '/auth/callback') {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Signup flow: existing user redirected to Welcome without auth (session flag set in Signup.js)
    if (basePath === '/welcome' && sessionStorage.getItem('mvp_welcome_existing_user') === '1') {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // SSO redirect with error (e.g. ?error=email_already_registered): show signup/signin so we can display the error
    if ((basePath === '/signup' || basePath === '/signin') && searchParams.get('error')) {
      sessionStorage.setItem(SSO_ERROR_VIEW_KEY, '1');
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Still on signup/signin after we removed ?error= from URL (message shows for 5s); keep allowing
    if ((basePath === '/signup' || basePath === '/signin') && sessionStorage.getItem(SSO_ERROR_VIEW_KEY) === '1') {
      setAllowed(true);
      setChecking(false);
      return;
    }
    // Leave signup/signin: clear SSO error view flag so next visit needs ref again
    if (basePath !== '/signup' && basePath !== '/signin') {
      sessionStorage.removeItem(SSO_ERROR_VIEW_KEY);
    }
    // Login is always reachable (post-logout, bookmarks, support). Partner ref is optional here; signup funnel still enforces ref.
    if (basePath === '/login') {
      setAllowed(true);
      setChecking(false);
      return;
    }
    const isAdvisorPath =
      basePath === '/advisor' ||
      basePath === '/admin' ||
      basePath === '/customer/new' ||
      /^\/customer\/[^/]+$/.test(basePath) ||
      /^\/case\/[^/]+$/.test(basePath) ||
      /^\/family\/[^/]+$/.test(basePath) ||
      /^\/advisor\/cases\/[^/]+\/[^/]+$/.test(basePath) ||
      /^\/admin\/cases\/[^/]+\/[^/]+$/.test(basePath);

    // App entry routes should be reachable without a partner ref.
    // Actual access control (logged-in vs not) is handled by route-level guards.
    if (basePath === '/intake' || basePath === '/organizer' || isAdvisorPath) {
      setAllowed(true);
      setChecking(false);
      return;
    }

    if (
      token &&
      (basePath === '/register' ||
        path === '/es/regularizacion/success' ||
        basePath === '/intake' ||
        basePath === '/organizer' ||
        isAdvisorPath ||
        basePath === '/signup/sso-details' ||
        basePath === '/signup/sso-contact')
    ) {
      setAllowed(true);
      setChecking(false);
      return;
    }

    const ref = getRefFromUrl();
    const storedValid = sessionStorage.getItem(VALID_REF_KEY);

    if (!ref || !ref.trim()) {
      // Sales landing paths always require ref in URL; other paths can use stored valid ref from earlier visit.
      if (isSalesLandingPath) {
        sessionStorage.removeItem(VALID_REF_KEY);
        setAllowed(false);
      } else if (storedValid === '1') {
        setAllowed(true);
      } else {
        setAllowed(false);
      }
      setChecking(false);
      return;
    }

    setChecking(true);
    mvpApi
      .getPartnerByToken(ref.trim())
      .then((res) => {
        if (res && res.status === 200) {
          sessionStorage.setItem(VALID_REF_KEY, '1');
          setAllowed(true);
        } else {
          sessionStorage.removeItem(VALID_REF_KEY);
          setAllowed(false);
        }
      })
      .catch(() => {
        sessionStorage.removeItem(VALID_REF_KEY);
        setAllowed(false);
      })
      .finally(() => setChecking(false));
  }, [location.pathname, location.search]);

  if (checking) {
    return (
      <div className="refguard-loading" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060C16',
        color: 'var(--muted)',
      }}>
        Checking access...
      </div>
    );
  }

  if (!allowed) return <NotFound404 />;
  return children;
}
